/**
 * Small formatting helpers shared by the CRM rows and cards.
 *
 * Two of these exist because of RTL and Israeli phone conventions specifically, and
 * getting them wrong is immediately visible to every user.
 */

/**
 * Renders an international id in the local form Israelis read: +972524765402 → 052-476-5402.
 *
 * Contact ids are stored international, but nobody says their number that way here. The
 * caller must render the result inside an LTR box — a phone number inside an RTL
 * paragraph otherwise puts the leading digits on the wrong side.
 *
 * That box MUST be the HTML `dir="ltr"` ATTRIBUTE, never `sx={{ direction: 'ltr' }}`:
 * the app wraps everything in stylis-plugin-rtl, which mirrors the CSS `direction`
 * property along with the physical ones, so an sx-declared `ltr` is emitted as
 * `direction: rtl` — measured on the shipped bundle. The attribute is not CSS, so it
 * survives, and the UA stylesheet gives `[dir]` a `unicode-bidi: isolate` for free.
 * Israeli local numbers hide the bug (a hyphen between digits is absorbed into the
 * numeric run by the bidi algorithm); the international fallback form below is where it
 * shows, with the leading `+` jumping to the far end.
 */
export const formatContactPhone = (contactId: string): string => {
  const digits = (contactId ?? "").replace(/[^0-9]/g, "");
  if (!digits) return contactId ?? "";

  // Israeli mobile/landline: 972XXXXXXXXX → 0XXXXXXXXX
  const local = digits.startsWith("972") ? `0${digits.slice(3)}` : digits;

  if (local.length === 10) return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
  if (local.length === 9) return `${local.slice(0, 2)}-${local.slice(2, 5)}-${local.slice(5)}`;
  // Anything else (a foreign number) keeps its international form rather than being
  // mangled into an Israeli shape.
  return contactId;
};

/** Two-letter monogram, falling back to the last digits so a nameless contact still has an anchor. */
export const contactInitials = (name: string | undefined | null, contactId: string): string => {
  const trimmed = (name ?? "").trim();
  if (trimmed) {
    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length >= 2) return `${words[0][0]}${words[1][0]}`;
    return words[0].slice(0, 2);
  }
  // 9% of contacts have no name at all. Identical avatars for all of them would make
  // the list unscannable, so the last two digits differentiate them.
  const digits = contactId.replace(/[^0-9]/g, "");
  return digits.slice(-2) || "?";
};

/**
 * A money amount the way an Israeli realtor writes one: `2.5 מיליון ₪`, `450 אלף ₪`,
 * `6,500 ₪`.
 *
 * THIS REPLACED `₪2.5M`, AND THE REASON IS A MEASURED RENDERING BUG, not taste.
 *
 * `₪2.5M` is three different bidi classes in five characters — ₪ is ET (European
 * Terminator), `2.5` is EN, `M` is a Latin L. Dropped into the Hebrew facts line it
 * produced, on screen (1440px, he-IL, measured):
 *
 *     logical:   תקציב ₪2.5M · 5 חד׳ · טירת כרמל
 *     displayed: תקציב 5 · 2.5M₪ חד׳ · טירת כרמל
 *
 * — the ₪ migrated to the far side of its own number, AND the budget swapped places
 * with the room count, so the realtor read "budget 5" and "2.5M rooms". Two numbers
 * attached to each other's labels is worse than no number at all.
 *
 * The Hebrew form has no Latin letters, so there is no LTR island to reorder: the
 * digits are the only non-Hebrew run and they sit inside a Hebrew phrase, which the
 * bidi algorithm resolves unambiguously. It is also what CLDR's he-IL currency format
 * does — the ₪ goes AFTER the amount in Hebrew, not before it — and what every Israeli
 * listing site prints. `M`/`K` are English abbreviations no Hebrew price list uses.
 *
 * Callers must still render each amount in its OWN element (see `CrmFactChip`): an
 * atomic inline-level box resolves its contents as an independent bidi run, which is
 * what stops a neighbouring fact from reordering this one.
 */
export const formatBudget = (value: number | null | undefined): string => {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return "";
  if (value >= 1_000_000) return `${trimDecimals(value / 1_000_000, 2)} מיליון ₪`;
  // 100k is the boundary between "a price" and "a rent". A ₪15,000 rent must not be
  // rounded to "15 אלף" — at that magnitude the exact number is the information.
  if (value >= 100_000) return `${groupDigits(Math.round(value / 1000))} אלף ₪`;
  return `${groupDigits(Math.round(value))} ₪`;
};

/** `5 חד׳` / `4-5.5 חד׳`. */
export const formatRooms = (min: number | null | undefined, max: number | null | undefined): string => {
  const low = typeof min === "number" ? min : max;
  const high = typeof max === "number" ? max : min;
  if (typeof low !== "number" || typeof high !== "number") return "";
  // An ASCII hyphen between two digit runs is a bidi European Separator and is absorbed
  // into the number, so `4-5.5` stays one LTR run. An en-dash is a NEUTRAL and would be
  // free to drift to the other end of the phrase — do not "typographically improve" it.
  return low === high ? `${low} חד׳` : `${low}-${high} חד׳`;
};

/** `2.5`, `4.75`, `3` — never `3.00`. */
const trimDecimals = (value: number, maxDecimals: number): string => {
  const fixed = value.toFixed(maxDecimals);
  return fixed.includes(".") ? fixed.replace(/0+$/, "").replace(/\.$/, "") : fixed;
};

/** Western digits with thousands commas, pinned to en-US so the digits can never
 *  come back as Eastern-Arabic numerals under a different runtime locale. */
const groupDigits = (value: number): string => value.toLocaleString("en-US");

export const telHref = (contactId: string): string => `tel:${contactId}`;

/** wa.me wants bare digits with the country code and no '+'. */
export const whatsappHref = (contactId: string): string => `https://wa.me/${(contactId ?? "").replace(/[^0-9]/g, "")}`;

/**
 * An EXACT moment — "14.3.2025 · 09:41".
 *
 * ── When to use this instead of {@link relativeHebrew} ──────────────────────────
 *
 * A relative string answers "is this fresh?" at a glance and is right for a table cell a
 * realtor scans. It cannot answer "who got this lead FIRST" — comparing "לפני 3 חוד׳"
 * against "לפני 4 חוד׳" across a list of colleagues is exactly the question the agency
 * panel exists for, and exactly the one a rounded string cannot settle. So that panel
 * shows this and demotes the relative form to the tooltip.
 *
 * ⚠️ ONLY FOR TIMESTAMPS THAT CAN CARRY THE WEIGHT. A precise time is a stronger claim
 * than a vague one, so it must not be rendered off a field that moves for reasons the
 * realtor did not cause — see {@link relativeHebrew} below, and the measurement recorded
 * on `CrmContactHolder.lastChangeTime`, which is why the server sends the LATER of two
 * columns rather than either alone.
 *
 * ALL-NUMERIC, with a "·" between the date and the time. A Hebrew month name beside
 * digits is a bidi run that reorders under RTL; digits and dots have no such hazard, and
 * the same format is what the contact timeline already prints for an older event, so the
 * two surfaces read as one.
 */
export const exactHebrew = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const at = new Date(iso);
  const ms = at.getTime();
  if (!Number.isFinite(ms)) return "";
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${at.getDate()}.${at.getMonth() + 1}.${at.getFullYear()} · ${pad(at.getHours())}:${pad(at.getMinutes())}`;
};

/**
 * "לפני 3 ימים" — deliberately coarse.
 *
 * Precision here would be false precision for THIS field: the row's `updatedTime` moves
 * on system writes, so an exact "14:02" would imply a human touched the record at that
 * minute. That reasoning is about the SOURCE, not about precision as such — where the
 * source can carry it, {@link exactHebrew} is the right renderer, and the holders payload
 * now sends a `lastChangeTime` built precisely so that it can.
 *
 * HEBREW COUNTS ITS NOUNS IN THREE, NOT TWO. Interpolating the number into a plural
 * noun is wrong for both 1 and 2: "לפני 1 שנים" is not a sentence, and it covered a
 * whole year-wide band of contacts — every record between 12 and 23 months old read
 * as broken text. 2 takes the dual form (שעתיים / יומיים / חודשיים / שנתיים), which
 * is not optional politeness in Hebrew but the ordinary way to say it. Only 3 and up
 * take a digit plus a plural, so those are the only branches that interpolate.
 * Minutes stop at the singular — "לפני 2 דק׳" is how an abbreviation is read aloud
 * and a dual there would be over-formal for a timestamp that refreshes constantly.
 */
export const relativeHebrew = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";

  const minutes = Math.floor((Date.now() - then) / 60000);
  if (minutes < 1) return "עכשיו";
  if (minutes === 1) return "לפני דקה";
  if (minutes < 60) return `לפני ${minutes} דק׳`;

  const hours = Math.floor(minutes / 60);
  if (hours === 1) return "לפני שעה";
  if (hours === 2) return "לפני שעתיים";
  if (hours < 24) return `לפני ${hours} שע׳`;

  const days = Math.floor(hours / 24);
  if (days === 1) return "אתמול";
  if (days === 2) return "לפני יומיים";
  if (days < 30) return `לפני ${days} ימים`;

  const months = Math.floor(days / 30);
  if (months === 1) return "לפני חודש";
  if (months === 2) return "לפני חודשיים";
  if (months < 12) return `לפני ${months} חוד׳`;

  const years = Math.floor(months / 12);
  if (years === 1) return "לפני שנה";
  if (years === 2) return "לפני שנתיים";
  return `לפני ${years} שנים`;
};
